import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { plainToClass } from 'class-transformer';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ProgressIndicatorService } from './progress-indicator.service';
import { CollectApiServiceBase } from './collect-api-base.service';
import { NotificationTemplate } from '../models/notifications/notification-template.model';
import { NotificationInstance } from '../models/notifications/notification-instance.model';
import { DataCollectionMilestone } from '../models/milestones/data-collection-milestone.model';
import { ContactsGridNotificationTemplateAssociations } from '../models/notifications/contacts-grid-notification-template-association.model';
import { ProtocolAndBaseUrlUtil } from '../utility-classes/protocol-and-base-url.util';
import { EmailGeneratedWithNotificationTemplate } from '../models/notifications/email-generated-w-notification-template.model';

@Injectable()
export class NotificationService extends CollectApiServiceBase<NotificationTemplate> {
    // Constructor.
    public constructor(http: HttpClient, progressIndicatorService: ProgressIndicatorService) {
        super(http, progressIndicatorService, environment.apiUrl, 'notification', NotificationTemplate)
    }

    // Overrides.
    formatResponse(data: NotificationTemplate): NotificationTemplate {
        let obj = plainToClass(NotificationTemplate, data);

        return (obj);
    }

    // Service methods.
    public findAllForCategory(category: string): Promise<NotificationTemplate[]> {
        let url = `${this.url}/${this.endpoint}/findAllForCategory/${category}`;

        //this.updateProgress(50, 75, `Getting notification templates ...`);

        let observable: Observable<NotificationTemplate[]> = this.http.get<NotificationTemplate[]>(url);

        return observable.toPromise()
            .then(response => {
                let templates: NotificationTemplate[] = [];

                if (response != null) {
                    for (let iTemplate: number = 0; iTemplate < response.length; iTemplate++) {
                        let oTemplate = response[iTemplate];
                        let template: NotificationTemplate = plainToClass(NotificationTemplate, oTemplate);

                        templates.push(template);
                    }
                }

                //this.updateProgress(100, 100, `Completed`);

                return (templates);
            })
            .catch(this.handleError);
    }

    public getSiteMilestoneTemplatePreview(milestone: DataCollectionMilestone): Promise<NotificationInstance> {
        let url = `${this.url}/${this.endpoint}/getSiteMilestoneTemplatePreview`;

        return this.http.post<DataCollectionMilestone>(url, milestone)
            .toPromise()
            .then(response => {
                let notification: NotificationInstance = null;

                if (response != null) {
                    notification = plainToClass(NotificationInstance, response);
                }

                return (notification);
            })
            .catch(this.handleError);
    }

    public getUserNotifications(dataCollectionId: number): Promise<NotificationInstance[]> {
        let url = `${this.url}/${this.endpoint}/getUserNotifications/${dataCollectionId}`;

        this.updateProgress(50, 75, `Getting notification templates ...`);

        let observable: Observable<NotificationInstance[]> = this.http.get<NotificationInstance[]>(url);

        return observable.toPromise()
            .then(response => {
                let notifications: NotificationInstance[] = [];

                if (response != null) {
                    for (let iNotification: number = 0; iNotification < response.length; iNotification++) {
                        let oNotification = response[iNotification];
                        let notification: NotificationInstance = plainToClass(NotificationInstance, oNotification);

                        notifications.push(notification);
                    }
                }

                this.updateProgress(100, 100, `Completed`);

                return (notifications);
            })
            .catch(this.handleError);
    }

    public getDefaultEmailNotificationTemplate(formTemplateId: number): Promise<NotificationTemplate> {
        let url = `${this.url}/${this.endpoint}/getDefaultEmailNotificationTemplate/${formTemplateId}`;

        return this.http.get<NotificationTemplate>(url)
            .toPromise()
            .then(response => {
                let result: NotificationTemplate = null;

                if (response != null) {
                    result = plainToClass(NotificationTemplate, response);
                }

                return result;
            })
            .catch(this.handleError);
    }

    public saveFormTemplateNotificationTemplate(notificationTemplate: NotificationTemplate): Promise<NotificationTemplate> {
        let result: Promise<NotificationTemplate> = notificationTemplate.id == 0 ? this.createFormTemplateNotificationTemplate(notificationTemplate) : this.updateFormTemplateNotificationTemplate(notificationTemplate);

        return result;
    }

    public createFormTemplateNotificationTemplate(notificationTemplate: NotificationTemplate): Promise<NotificationTemplate> {
        let url = `${this.url}/${this.endpoint}/createFormTemplateNotificationTemplate`;

        return this.http.post<DataCollectionMilestone>(url, notificationTemplate)
            .toPromise()
            .then(response => {
                let result: NotificationTemplate = null;

                if (response != null) {
                    result = plainToClass(NotificationTemplate, response);
                }

                return result;
            })
            .catch(this.handleError);
    }

    public updateFormTemplateNotificationTemplate(notificationTemplate: NotificationTemplate): Promise<NotificationTemplate> {
        let url = `${this.url}/${this.endpoint}/updateFormTemplateNotificationTemplate`;

        return this.http.put<NotificationTemplate>(url, notificationTemplate)
            .toPromise()
            .then(response => {
                let result: NotificationTemplate = null;

                if (response != null) {
                    result = plainToClass(NotificationTemplate, response);
                }

                return result;
            })
            .catch(this.handleError);
    }

    public deleteFormTemplateNotificationTemplate(notificationTemplateId: number): Promise<boolean> {
        let url = `${this.url}/${this.endpoint}/deleteFormTemplateNotificationTemplate/${notificationTemplateId}`;
        return this.http.delete<any[]>(url)
            .toPromise()
            .then(res => {
                return res;
            })
            .catch(this.handleError);
    }

    public getFormTemplateNotificationTemplates(formTemplateId: number): Promise<NotificationTemplate[]> {
        let url = `${this.url}/${this.endpoint}/getFormTemplateNotificationTemplates/${formTemplateId}`;

        return this.http.get<NotificationTemplate[]>(url)
            .toPromise()
            .then(response => {
                let result: NotificationTemplate[] = [];

                if (response != null) {
                    for (let index: number = 0; index < response.length; index++) {
                        let template = plainToClass(NotificationTemplate, response[index]);
                        result.push(template);
                    }                    
                }

                return result;
            })
            .catch(this.handleError);
    }

    public associateNotificationTemplatesWithContactsFormField(contactsGridAssocations: ContactsGridNotificationTemplateAssociations): Promise<ContactsGridNotificationTemplateAssociations> {
        let url = `${this.url}/${this.endpoint}/associateNotificationTemplatesWithContactsFormField`;

        return this.http.put<ContactsGridNotificationTemplateAssociations>(url, contactsGridAssocations)
            .toPromise()
            .then(response => {
                let result: ContactsGridNotificationTemplateAssociations = null;

                if (response != null) {
                    result = plainToClass(ContactsGridNotificationTemplateAssociations, response);
                }

                return result;
            })
            .catch(this.handleError);
    }

    public getContactsTemplateNotificationTemplateAssociations(contactsGridFormFieldId: number, formInstanceId: number): Promise<ContactsGridNotificationTemplateAssociations> {
        let url = `${this.url}/${this.endpoint}/getContactsTemplateNotificationTemplateAssociations/${contactsGridFormFieldId}/${formInstanceId}`;

        return this.http.get<ContactsGridNotificationTemplateAssociations>(url)
            .toPromise()
            .then(response => {
                let result: ContactsGridNotificationTemplateAssociations = null;

                if (response != null) {
                    result = plainToClass(ContactsGridNotificationTemplateAssociations, response);
                }

                return result;
            })
            .catch(this.handleError);
    }
    
    public generateFormInstanceEmailUsingTemplate(formInstanceId: number, notificationTemplateId: number): Promise<EmailGeneratedWithNotificationTemplate> {
        const protocolAndBaseUrl = ProtocolAndBaseUrlUtil.getProtocolAndBaseUrl();

        let url = `${this.url}/${this.endpoint}/generateFormInstanceEmailUsingTemplate/${formInstanceId}/${notificationTemplateId}/${protocolAndBaseUrl.protocol}/${protocolAndBaseUrl.baseurl}`;

        return this.http.get<EmailGeneratedWithNotificationTemplate>(url)
            .toPromise()
            .then(response => {
                let result: EmailGeneratedWithNotificationTemplate = null;

                if (response != null) {
                    result = plainToClass(EmailGeneratedWithNotificationTemplate, response);
                }

                return result;
            })
            .catch(this.handleError);
    }
}
